import { z } from 'zod';

/**
 * CompanyId: Branded Type for UUID company identifiers
 *
 * This branded type ensures type safety at compile time while maintaining UUID string values.
 * Using this type everywhere prevents accidental numeric company_id usage and makes the
 * intent clear: this is a company UUID, not a number.
 *
 * Usage:
 *   const companyId: CompanyId = brandCompanyId('690eaf16-063d-547b-9109-08e6a70d5d69');
 *   const companyId = parseCompanyId(formData.company_id); // Safe with null checking
 *
 * Benefits:
 * - Single source of truth for company_id validation
 * - Prevents future numeric conversions (parseInt never happens)
 * - Enables compliance features (encryption, logging) in ONE place
 * - Future changes: edit THIS file, not 40+ calling files
 */
export type CompanyId = string & { readonly __brand: 'CompanyId' };

/**
 * Internal branding function - validates UUID format and returns branded type
 */
function brandCompanyId(id: string): CompanyId {
  // Validate UUID format: 8-4-4-4-12 hex digits
  if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id)) {
    throw new Error(`Invalid company_id format: ${id}`);
  }
  return id as CompanyId;
}

/**
 * ZOD SCHEMA: Single source of truth for company_id validation
 *
 * Use this schema in all Zod validation chains:
 *   z.object({ company_id: CompanyIdSchema.nullable() })
 *
 * This ensures consistent validation across frontend, API routes, and webhooks.
 * If validation rules change (e.g., require non-null), update here and ALL
 * dependent code automatically inherits the change.
 */
export const CompanyIdSchema = z.string().uuid('Company ID must be a valid UUID');
export type CompanyIdInput = z.infer<typeof CompanyIdSchema>;

/**
 * SAFE PARSING: Parse unknown values into CompanyId with null handling
 *
 * Returns null if:
 * - Value is null/undefined/empty string
 * - Value fails UUID validation
 *
 * Use this for form inputs, query params, and untrusted sources.
 *
 * Example:
 *   const companyId = parseCompanyId(form.company_id);
 *   if (!companyId) return errorResponse('Invalid company selection');
 */
export function parseCompanyId(value: unknown): CompanyId | null {
  if (!value) return null;

  const result = CompanyIdSchema.safeParse(value);
  if (!result.success) return null;

  try {
    return brandCompanyId(result.data);
  } catch {
    return null;
  }
}

/**
 * STRICT PARSING: Parse or throw error
 *
 * Use only when invalid input should crash the system
 * (e.g., during server startup, not request handling).
 *
 * Example:
 *   const envCompanyId = parseCompanyIdOrThrow(process.env.DEFAULT_COMPANY_ID);
 */
export function parseCompanyIdOrThrow(value: unknown): CompanyId {
  const result = parseCompanyId(value);
  if (!result) throw new Error(`Invalid company_id: ${String(value)}`);
  return result;
}

/**
 * ACCESS CONTROL: Centralized authorization logic
 *
 * Handles both regular users (company-scoped) and admins (unrestricted).
 * This is the ONLY place authorization decisions should be made.
 *
 * Usage:
 *   const allowed = isCompanyAuthorized(userCompanyId, targetCompanyId, user.role === 'superadmin');
 *   if (!allowed) return c.json({ error: 'Unauthorized' }, 403);
 *
 * Future enhancement: Add audit logging here automatically
 */
export function isCompanyAuthorized(
  userCompanyId: CompanyId | null,
  targetCompanyId: CompanyId | null,
  isAdmin = false
): boolean {
  // Superadmins can access any company
  if (isAdmin) return true;

  // Regular users can only access their company
  return userCompanyId === targetCompanyId;
}

/**
 * FORM HANDLING: Extract company_id from form data safely
 *
 * Replaces the dangerous pattern: parseInt(form.company_id, 10)
 * That pattern converts UUID strings to NaN, breaking database inserts.
 *
 * Usage:
 *   const companyId = extractCompanyIdFromForm(form);
 *   if (!companyId && form.company_id !== '') {
 *     setError('Invalid company selection');
 *     return;
 *   }
 */
export function extractCompanyIdFromForm(
  formData: Record<string, unknown>
): CompanyId | null {
  const raw = formData['company_id'];

  // Empty string or null = user didn't select anything
  if (raw === '' || raw === null || raw === undefined) return null;

  return parseCompanyId(raw);
}

/**
 * TYPE GUARD: Check if value is valid CompanyId at runtime
 *
 * Useful for type narrowing in conditionals or asserts.
 *
 * Usage:
 *   if (isValidCompanyId(value)) {
 *     // value is now typed as CompanyId
 *     const result = await db.query().eq('company_id', value);
 *   }
 */
export function isValidCompanyId(value: unknown): value is CompanyId {
  return (
    typeof value === 'string' &&
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value)
  );
}

/**
 * COMPARISON: Strict string equality for company IDs
 *
 * Used to compare company IDs for access control.
 * Type system ensures both values are CompanyId, not arbitrary strings.
 *
 * Usage:
 *   if (sameCompany(userCompanyId, targetCompanyId)) {
 *     // do something
 *   }
 */
export function sameCompany(companyId1: CompanyId | null, companyId2: CompanyId | null): boolean {
  return companyId1 === companyId2;
}
